import { getCompanies } from 'API/company';
import { CompanySearchTypes, TenantSortBy } from 'features/account/account-management/account-management.types';
import { useUserData } from 'hooks/use-user-data';
import { useState } from 'react';
import useErrorStore from 'stores/error-store';
import { Company } from '../../../../types/company.types';

const useCompanies = (resetOpenTenantId?: () => void) => {
  useUserData();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState<number>(1);
  const [searchParam, setSearchParam] = useState('');
  const [searchType, setSearchType] = useState<CompanySearchTypes>(CompanySearchTypes.USER);
  const [sortBy, setSortBy] = useState<TenantSortBy>(TenantSortBy.CREATED_AT_DESC);

  const itemsPerPage = 10;
  let typingTimer: ReturnType<typeof setTimeout>;

  const fetchCompanies = async (
    newPage: number = page,
    newSearchParam: string = searchParam,
    newSearchType: CompanySearchTypes = searchType,
    newSortBy: TenantSortBy = sortBy,
    preventResetOpenTenantId: boolean = false
  ) => {
    setLoading(true);
    const [sortAttribute, sortDirection] = newSortBy.split(':');
    const res = await getCompanies(newPage - 1, itemsPerPage, sortAttribute, sortDirection, newSearchParam, newSearchType);
    if (!res.error && res.data) {
      const normalizedCompanies = (res.data.companies || []).map((company: any) => new Company(company));
      setCompanies(normalizedCompanies);
      setTotal(res.data.total);
      setPage(newPage);
      if (!preventResetOpenTenantId) {
        resetOpenTenantId?.();
      }
    } else {
      setCompanies([]);
      setTotal(0);
      setPage(1);
      handleOpenErrorDialog(res?.error, '/tenants/admin/all', 'get');
    }
    setLoading(false);
  };

  /**
   * Search handlers
   */

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchCompanies = async () => {
    fetchCompanies(1, searchParam);
  };

  const searchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleSearchCompanies();
    }, 500);
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const handleClearSearchField = () => {
    setSearchParam('');
    fetchCompanies(1, '');
  };

  const handleSearchTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchType(event.target.value as CompanySearchTypes);

    if (searchParam) {
      fetchCompanies(1, searchParam, event.target.value as CompanySearchTypes);
    }
  };

  /**
   * Pagination handlers
   */

  const handleChangePage = (event: unknown, newPage: number) => {
    fetchCompanies(newPage);
  };

  const resetCompanies = () => {
    setPage(1);
    setSearchParam('');
    setSearchType(CompanySearchTypes.USER);
    fetchCompanies(1, '', CompanySearchTypes.USER);
  };

  /**
   * Sorting handlers
   */

  const handleSortByChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value  } = event.target;
    setSortBy(value as TenantSortBy);
    fetchCompanies(undefined, undefined, undefined, value as TenantSortBy);
  };

  return {
    companies,
    total,
    loading,
    itemsPerPage,
    page,
    searchParam,
    searchType,
    sortBy,
    handleSearchParamChange,
    searchKeyUp,
    searchKeyDown,
    handleClearSearchField,
    handleSearchTypeChange,
    handleChangePage,
    handleSortByChange,
    fetchCompanies,
    resetCompanies,
  };
};

export default useCompanies;
